import React, { useState, useRef } from 'react';
import { X } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { setCustomerModules, fetchCustomers } from '../redux/adminSlice';
import {
  TENANT_MODULE_SECTIONS,
  normalizeTenantModules,
  LINKED_WEB_MOBILE_KEYS,
  LINKED_COMBINED_LABELS,
} from './tenantModulesCatalog';

// Per-customer "Modules" panel: lets the super-admin turn web modules,
// mobile app features, and premium (restricted) pages on/off for one
// customer. Absent map/key always renders CHECKED (enabled) - see
// normalizeTenantModules. Save always sends the FULL explicit
// {web,mobile,restricted} boolean map for today's catalog; only
// adminDashboardF-setCustomerModules is allowed to write customers/{cid}.modules.
const CustomerModulesModal = ({ customer, onClose }) => {
  const dispatch = useDispatch();
  const [modules, setModules] = useState(() => normalizeTenantModules(customer?.modules));
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);

  if (!customer) {
    return null;
  }

  // Linked web+mobile keys toggle BOTH spaces together - the server's
  // permission lists pair "web:<key>" with "mobile:<key>" on shared
  // callables and access passes when EITHER side is enabled, so splitting
  // them would promise per-surface blocking the server can't deliver (see
  // LINKED_WEB_MOBILE_KEYS in tenantModulesCatalog.js).
  const toggleKey = (space, key) => {
    setModules((prev) => {
      const next = !prev[space][key];
      const out = {
        ...prev,
        [space]: { ...prev[space], [key]: next },
      };
      if (space === 'web' && LINKED_WEB_MOBILE_KEYS.includes(key)) {
        out.mobile = { ...prev.mobile, [key]: next };
      }
      return out;
    });
  };

  const isLinkedRow = (section, item) =>
    section.space === 'web' && LINKED_WEB_MOBILE_KEYS.includes(item.key);

  const displayLabel = (section, item) =>
    isLinkedRow(section, item)
      ? LINKED_COMBINED_LABELS[item.key] || item.label
      : item.label;

  const itemsForSection = (section) =>
    section.space === 'mobile'
      ? section.items.filter((item) => !LINKED_WEB_MOBILE_KEYS.includes(item.key))
      : section.items;

  const handleSave = async () => {
    if (savingRef.current) {
      return; // sync guard: a double-click can never double-submit
    }
    savingRef.current = true;
    setIsSaving(true);
    try {
      await dispatch(
        setCustomerModules({ customerId: customer.id, modules })
      ).unwrap();
      // setCustomerModules.fulfilled already patches this customer's
      // `modules` locally (see adminSlice.js), so the table reflects the
      // save immediately. Also refresh via the existing fetch flow for
      // cross-admin consistency (fetchCustomers returns each customer's
      // full doc, modules included).
      dispatch(fetchCustomers());
      onClose();
      return;
    } catch (err) {
      savingRef.current = false;
      setIsSaving(false);
      alert(typeof err === 'string' ? err : 'Failed to update modules. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-[rgba(150,165,180,.25)] bg-white shadow-[0_12px_40px_rgba(29,43,51,.35)]">
        <div className="flex flex-none items-center justify-between gap-4 rounded-t-xl bg-[linear-gradient(120deg,#4a5c70,#374a5e_55%,#2a3a4c)] px-5 py-4">
          <div className="min-w-0">
            <h3 className="truncate text-lg font-bold text-white">Modules</h3>
            <p className="truncate text-sm text-[rgba(215,226,236,.75)]">{customer.name || customer.email}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="flex-none rounded-lg border border-white/25 p-1.5 text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {TENANT_MODULE_SECTIONS.map((section) => (
            <div key={section.space} className="mb-6 last:mb-0">
              <h4 className="mb-2 text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">
                {section.title}
              </h4>
              {section.space === 'mobile' && (
                <p className="-mt-1 mb-2 text-xs text-[#64798a]">
                  Hides these features in the worker app. Features shared with
                  the web (warehouse, chat, documents, walkthroughs) are the
                  combined switches above.
                </p>
              )}
              <div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
                {itemsForSection(section).map((item) => (
                  <label
                    key={item.key}
                    className="flex items-center gap-2 rounded-lg border border-[rgba(150,165,180,.2)] bg-white p-2 text-sm text-[#1d2b33] transition hover:bg-[#f4f7fa]"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-[#2f66b8]"
                      checked={modules[section.space][item.key]}
                      onChange={() => toggleKey(section.space, item.key)}
                    />
                    <span className="flex-1">{displayLabel(section, item)}</span>
                    {isLinkedRow(section, item) && (
                      <span className="flex-none rounded-full bg-[#eef2f6] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#64798a]">
                        web + mobile
                      </span>
                    )}
                    {item.addOn && (
                      <span className="flex-none rounded-full bg-[#dcebfb] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#2f66b8]">
                        Add-on
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-none items-center justify-end gap-2 border-t border-[rgba(150,165,180,.25)] bg-white px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="rounded-lg border border-[rgba(150,165,180,.4)] bg-white px-4 py-2 text-sm font-semibold text-[#1d2b33] transition hover:bg-[#f4f7fa] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="rounded-lg bg-[#2f66b8] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#285aa5] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerModulesModal;
